import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface PlanLimits {
  tier: string;
  meetingsUsed: number;
  meetingsLimit: number; // -1 = unlimited
  meetingsRemaining: number; // -1 = unlimited
  canCreateMeeting: boolean;
  /** Maximum recording length in seconds. -1 = unlimited */
  recordingMaxSeconds: number;
  /** Recording retention in days. -1 = unlimited */
  recordingRetentionDays: number;
  /** Maximum participants per meeting */
  maxParticipants: number;
  loading: boolean;
  refetch: () => Promise<void>;
}

const TIER_LIMITS: Record<string, Omit<PlanLimits, "loading" | "refetch" | "tier" | "meetingsUsed" | "meetingsRemaining" | "canCreateMeeting">> = {
  free: {
    meetingsLimit: 10,
    recordingMaxSeconds: 60 * 60, // 1 hour
    recordingRetentionDays: 2,
    maxParticipants: 100,
  },
  starter: {
    meetingsLimit: -1,
    recordingMaxSeconds: 4 * 60 * 60,
    recordingRetentionDays: 30,
    maxParticipants: 100,
  },
  professional: {
    meetingsLimit: -1,
    recordingMaxSeconds: -1,
    recordingRetentionDays: 90,
    maxParticipants: 100,
  },
  business: {
    meetingsLimit: -1,
    recordingMaxSeconds: -1,
    recordingRetentionDays: 365,
    maxParticipants: 100,
  },
};

export function usePlanLimits(): PlanLimits {
  const { user } = useAuth();
  const [tier, setTier] = useState<string>("free");
  const [used, setUsed] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const fetch = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await (supabase.rpc as any)("can_create_meeting", { _user_id: user.id });
      if (error) throw error;
      const result = data as { tier: string; used: number; limit: number; allowed: boolean };
      setTier(result?.tier || "free");
      setUsed(result?.used || 0);
    } catch (e) {
      console.error("usePlanLimits error:", e);
      setTier("free");
      setUsed(0);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const cfg = TIER_LIMITS[tier] || TIER_LIMITS.free;
  const meetingsRemaining = cfg.meetingsLimit === -1 ? -1 : Math.max(0, cfg.meetingsLimit - used);
  const canCreateMeeting = cfg.meetingsLimit === -1 || used < cfg.meetingsLimit;

  return {
    tier,
    meetingsUsed: used,
    meetingsLimit: cfg.meetingsLimit,
    meetingsRemaining,
    canCreateMeeting,
    recordingMaxSeconds: cfg.recordingMaxSeconds,
    recordingRetentionDays: cfg.recordingRetentionDays,
    maxParticipants: cfg.maxParticipants,
    loading,
    refetch: fetch,
  };
}
